import {Box, Button, Divider, Switch, Typography} from "@mui/material";
import Dialog from "../shared/Dialog";
import {useState, useEffect} from "react";
import {updateProgramSettings} from "../../services/PlanSerice";
import {useMessageContext} from "../../contexts/MessageContext";
import {getErrorMessage} from "../../enums/ErrorMessages";

const ProgramSettingsDialog = ({isOpen, close, client, program, handleReload}) => {
  const {notificationHandler} = useMessageContext();

  const [settings, setSettings] = useState({
    automatic: program?.automaticProgression ?? false,
    useProgressionAlgorithm: program?.useProgressionAlgorithm ?? true,
    useRir: program?.showRir ?? true
  });

  // Update settings when program changes or dialog opens
  useEffect(() => {
    if (program && isOpen) {
      setSettings({
        automatic: program.automaticProgression ?? false,
        useProgressionAlgorithm: program.useProgressionAlgorithm ?? true,
        useRir: program.showRir ?? true
      });
    }
  }, [program, isOpen]);

  const handleSave = () => {
    updateProgramSettings(client.id, {
      automatic: settings.automatic,
      useProgressionAlgorithm: settings.useProgressionAlgorithm,
      useRir: settings.useRir
    })
      .then(() => {
        notificationHandler.success("הגדרות נשמרו בהצלחה");
        handleReload();
        close();
      })
      .catch(e => notificationHandler.error(getErrorMessage(e.response?.data?.errorCode)));
  };

  const SettingItem = ({title, description, checked, onChange, label}) => (
    <Box sx={{
      mb: 2,
      backgroundColor: "#F8FAFC",
      borderRadius: "12px",
      p: 2
    }}>
      <Box sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 1
      }}>
        <Typography sx={{
          fontSize: "15px",
          fontWeight: 600,
          color: "#1E293B"
        }}>
          {title}
        </Typography>
        <Box sx={{display: "flex", alignItems: "center", gap: 0.5}}>
          <Switch
            checked={checked}
            onChange={onChange}
            size="small"
            sx={{
              '& .MuiSwitch-switchBase.Mui-checked': {
                color: '#57837E',
              },
              '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                backgroundColor: '#57837E',
              },
            }}
          />
          <Typography sx={{
            fontSize: "13px",
            color: "#64748B",
            minWidth: "50px"
          }}>
            {label}
          </Typography>
        </Box>
      </Box>
      <Divider sx={{mb: 1.5}}/>
      <Typography sx={{
        fontSize: "13px",
        color: "#64748B",
        lineHeight: 1.6,
        textAlign: "right"
      }}>
        {description}
      </Typography>
    </Box>
  );

  return (
    <Dialog
      title={
        <Box sx={{ textAlign: "center" }}>
          <Typography sx={{
            fontSize: "22px",
            fontWeight: 600,
            color: "#57837E",
            mb: 0.5
          }}>
            הגדרות תוכנית
          </Typography>
          <Typography sx={{
            fontSize: "14px",
            color: "#64748B",
            fontWeight: 400
          }}>
            קבע כיצד התוכנית תתנהג עבור הספורטאי הזה.
          </Typography>
        </Box>
      }
      close={close}
      isOpen={isOpen}
      showCloseButton={true}
      narrow={true}
      actions={
        <Box sx={{display: "flex", gap: 2, justifyContent: "center"}}>
          <Button
            onClick={close}
            variant="outlined"
            sx={{
              color: "#64748B",
              borderColor: "#E2E8F0",
              px: 4,
              "&:hover": {
                borderColor: "#CBD5E1",
                backgroundColor: "#F8FAFC"
              }
            }}
          >
            ביטול
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            sx={{
              backgroundColor: "#57837E",
              px: 4,
              "&:hover": {
                backgroundColor: "#456b67"
              }
            }}
          >
            שמור הגדרות
          </Button>
        </Box>
      }
    >
      <Box sx={{
        direction: "rtl",
        p: 2
      }}>
        <SettingItem
          title="התקדמות אוטומטית"
          label={settings.automatic ? "התקדמות אוטומטית" : "התקדמות ידנית"}
          checked={settings.automatic}
          onChange={(e) => setSettings(prev => ({...prev, automatic: e.target.checked}))}
          description="כאשר מופעל והמתאמן מסיים את השבוע יפתח לו שבוע חדש ללא צורך בעדכון. כאשר מבוטל, כל התקדמות חייבת להיות מותאמת ידנית על ידי המאמן."
        />

        <SettingItem
          title="שימוש באלגוריתם התקדמות"
          label={settings.useProgressionAlgorithm ? "מופעל" : "מבוטל"}
          checked={settings.useProgressionAlgorithm}
          onChange={(e) => setSettings(prev => ({...prev, useProgressionAlgorithm: e.target.checked}))}
          description="כאשר הוא מופעל המערכת תקדם את המתאמן בהתאם לרמות המאמץ. כאשר הוא לא מופעל המערכת תחזיר לו בדיוק את מה שביצע בשבוע הקודם."
        />

        <SettingItem
          title="שימוש RIR"
          label={settings.useRir ? "RIR פעיל" : "מבוטל"}
          checked={settings.useRir}
          onChange={(e) => setSettings(prev => ({...prev, useRir: e.target.checked}))}
          description="כאשר RIR פעיל, ספורטאים ידווחו על המרחק מכשל עבור כל סט. כאשר מבוטל, שדות RIR מוסתרים ואינם נדרשים."
        />
      </Box>
    </Dialog>
  );
};

export default ProgramSettingsDialog;
